import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import AdminLayout from '../AdminLayout';
import {
  useGetCaseByIdQuery,
  useSendAdminMessageMutation
} from 'store/services/case/caseApi';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import AdminActionsPanel from './components/AdminActionsPanel';
import AdminEditTradeItemModal from '../Trades/components/AdminEditTradeItemModal';
import { AdminTrade, AdminTradeItem } from 'store/services/trade/tradeApi';
import { Points } from 'components/Points/Points';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import './CaseDetailPage.css';

export default function CaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [adminMessage, setAdminMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminTradeItem | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, refetch } = useGetCaseByIdQuery(caseId!);
  const [sendAdminMessage, { isLoading: isSending }] = useSendAdminMessageMutation();

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (data?.messages) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages]);

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = adminMessage.substring(0, start) + emoji + adminMessage.substring(end);

    setAdminMessage(newMessage);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const handleSendMessage = async () => {
    if (!adminMessage.trim() || !caseId) return;

    try {
      await sendAdminMessage({
        caseId,
        content: adminMessage.trim()
      }).unwrap();

      setAdminMessage('');
      toast.success('Message sent to both parties');
    } catch (error: any) {
      console.error('Failed to send admin message:', error);
      toast.error(error.data?.message || 'Failed to send message');
    }
  };

  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <AdminLayout title="Case Detail" currentPath="/admin/cases">
        <div className="case-detail-page">
          <div className="case-detail-loading">Loading case details...</div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout title="Case Detail" currentPath="/admin/cases">
        <div className="case-detail-page">
          <div className="case-detail-error">
            {(error as any)?.status === 404
              ? "This case doesn't exist or you don't have access."
              : 'Failed to load case details. Please try again.'}
          </div>
          <button onClick={() => navigate('/admin/cases')} className="back-button">
            Back to Cases
          </button>
        </div>
      </AdminLayout>
    );
  }

  const { case: caseDetails, messages, dividerTimestamp } = data;
  const trade = caseDetails.trade;

  // Split messages by divider timestamp
  const messagesBeforeDivider = messages.filter(
    m => new Date(m.createdAt) < new Date(dividerTimestamp)
  );
  const messagesAfterDivider = messages.filter(
    m => new Date(m.createdAt) >= new Date(dividerTimestamp)
  );

  return (
    <AdminLayout title="Case Detail" currentPath="/admin/cases">
      <div className="case-detail-page">
        {/* Header */}
        <div className="case-detail-header">
          <button onClick={() => navigate('/admin/cases')} className="back-button">
            ← Back to Cases
          </button>
          <div className="case-detail-header-info">
            <h1 className="case-detail-title">Case: {trade.tradeNumber}</h1>
            <span className={`case-status-badge status-${caseDetails.status.toLowerCase()}`}>
              {caseDetails.status}
            </span>
          </div>
        </div>

        {/* Trade Details Panel */}
        <div className="trade-details-panel">
          <h2 className="panel-title">Trade Details</h2>

          <div className="trade-parties">
            <div className="trade-party">
              <span className="party-label">Sender:</span>
              <div className="party-info">
                <AvatarWithFlag
                  avatarUrl={trade.sender.avatarUrl}
                  username={trade.sender.username}
                  size={48}
                />
                <div>
                  <div className="party-username">{trade.sender.username}</div>
                  <div className="party-email">{trade.sender.email}</div>
                </div>
              </div>
            </div>

            <div className="trade-arrow">→</div>

            <div className="trade-party">
              <span className="party-label">Receiver:</span>
              <div className="party-info">
                <AvatarWithFlag
                  avatarUrl={trade.receiver.avatarUrl}
                  username={trade.receiver.username}
                  size={48}
                />
                <div>
                  <div className="party-username">{trade.receiver.username}</div>
                  <div className="party-email">{trade.receiver.email}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="trade-meta">
            <div className="trade-meta-item">
              <span className="meta-label">Trade Status:</span>
              <span className="meta-value">{trade.status}</span>
            </div>
            <div className="trade-meta-item">
              <span className="meta-label">Trade Date:</span>
              <span className="meta-value">{formatDate(trade.createdAt)}</span>
            </div>
            <div className="trade-meta-item">
              <span className="meta-label">Total Value:</span>
              <Points points={trade.totalValue} type="small" />
            </div>
            <div className="trade-meta-item">
              <span className="meta-label">Case Date:</span>
              <span className="meta-value">{formatDate(caseDetails.createdAt)}</span>
            </div>
          </div>

          <div className="trade-items">
            <h3 className="items-title">Items in Trade ({trade.items.length})</h3>
            <div className="items-list">
              {trade.items.map((item, idx) => (
                <div key={idx} className="trade-item">
                  <span className="item-qty">{item.quantity}x</span>
                  <span className="item-name">{item.name}</span>
                  <span className="item-set">{item.set || item.setCode}</span>
                  <span className="item-condition">{item.condition}</span>
                  {item.foil && <span className="item-foil">Foil</span>}
                  <Points points={item.pointValue * item.quantity} type="small" />
                  {item.id && (
                    <button
                      className="item-edit-button"
                      onClick={() => {
                        const adaptedItem: AdminTradeItem = {
                          id: item.id!,
                          name: item.name,
                          set: item.set || item.setName || '',
                          setCode: item.setCode || '',
                          scryfallId: item.scryfallID || '',
                          quantity: item.quantity,
                          condition: item.condition,
                          language: item.language || 'en',
                          foil: item.foil ?? false,
                          pointValue: item.pointValue,
                        };
                        setEditingItem(adaptedItem);
                        setEditModalOpen(true);
                      }}
                      title="Edit trade item"
                    >
                      Edit
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Conversation Thread */}
        <div className="conversation-panel">
          <h2 className="panel-title">Conversation</h2>

          <div className="messages-container">
            {/* Messages before admin summoned */}
            {messagesBeforeDivider.length > 0 && (
              <div className="messages-section">
                <div className="section-label">Context (before admin summoned)</div>
                {messagesBeforeDivider.map((message) => (
                  <div
                    key={message.id}
                    className={`message-bubble ${
                      message.sender.isAdminMessage ? 'admin-message' : 'user-message'
                    }`}
                  >
                    <div className="message-sender-info">
                      <AvatarWithFlag
                        avatarUrl={message.sender.avatarUrl}
                        username={message.sender.username}
                        size={28}
                      />
                      <span className="message-sender">{message.sender.username}</span>
                      <span className="message-time">{formatMessageTime(message.createdAt)}</span>
                    </div>
                    <div className="message-content">{message.content}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Divider */}
            <div className="admin-summon-divider">
              <div className="divider-line"></div>
              <div className="divider-text">
                ADMIN SUMMONED ON {formatDate(dividerTimestamp)}
              </div>
              <div className="divider-line"></div>
            </div>

            {/* Messages after admin summoned */}
            <div className="messages-section">
              {messagesAfterDivider.length === 0 ? (
                <div className="empty-messages">
                  No messages yet. The conversation will appear here once the parties communicate.
                </div>
              ) : (
                messagesAfterDivider.map((message) => (
                  <div
                    key={message.id}
                    className={`message-bubble ${
                      message.sender.isAdminMessage ? 'admin-message' : 'user-message'
                    }`}
                  >
                    <div className="message-sender-info">
                      {message.sender.isAdminMessage ? (
                        <img
                          src={AssetUrls.logos.iconLogo}
                          alt="TradeMagic Admin"
                          className="message-avatar"
                        />
                      ) : (
                        <AvatarWithFlag
                          avatarUrl={message.sender.avatarUrl}
                          username={message.sender.username}
                          size={28}
                        />
                      )}
                      <span className="message-sender">
                        {message.sender.isAdminMessage ? 'TradeMagic Admin' : message.sender.username}
                      </span>
                      <span className="message-time">{formatMessageTime(message.createdAt)}</span>
                    </div>
                    <div className="message-content">{message.content}</div>
                  </div>
                ))
              )}
            </div>
            <div ref={messagesEndRef} />
          </div>

          {/* Admin Message Input */}
          <div className="admin-message-section">
            <h3 className="message-section-title">Send Message to Both Parties</h3>
            <div className="message-input-group">
              <textarea
                ref={textareaRef}
                className="message-textarea"
                placeholder="Type your message here..."
                value={adminMessage}
                onChange={(e) => setAdminMessage(e.target.value)}
                rows={4}
                maxLength={2000}
              />
              <CharacterCounter
                currentLength={adminMessage.length}
                maxLength={2000}
                warningThreshold={1800}
              />
            </div>

            <div className="message-actions">
              <div className="emoji-picker-container">
                <button
                  ref={emojiButtonRef}
                  className="emoji-picker-button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  type="button"
                >
                  😀
                </button>
                {showEmojiPicker && (
                  <EmojiPicker
                    onEmojiSelect={handleEmojiSelect}
                    onClose={() => setShowEmojiPicker(false)}
                    buttonRef={emojiButtonRef}
                  />
                )}
              </div>

              <button
                onClick={handleSendMessage}
                disabled={!adminMessage.trim() || isSending}
                className="send-message-button"
              >
                {isSending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>

        {/* Admin Actions Panel */}
        <AdminActionsPanel caseId={caseId!} caseStatus={caseDetails.status} />

        {/* Edit Trade Item Modal */}
        {editModalOpen && editingItem && (
          <AdminEditTradeItemModal
            trade={{
              id: trade.id,
              tradeNumber: trade.tradeNumber,
              status: trade.status as AdminTrade['status'],
              senderId: trade.sender.id,
              senderUsername: trade.sender.username,
              senderAvatar: trade.sender.avatarUrl,
              receiverId: trade.receiver.id,
              receiverUsername: trade.receiver.username,
              receiverAvatar: trade.receiver.avatarUrl,
              totalValue: trade.totalValue,
              initiatedAt: trade.createdAt,
              items: [],
            }}
            item={editingItem}
            onClose={() => {
              setEditModalOpen(false);
              setEditingItem(null);
              refetch();
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
